import { VStack, Button, Stack, Text } from '@chakra-ui/react';
import Joi from 'joi';
import { useAuthApi } from 'hooks/api/backend/auth/useAuthApi';
import { PageContext } from 'components/common/PageContext';
import { ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { CheckIconLarge } from 'assets/common/CheckIconLarge';
import { EmailIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { InAppLayoutVariant } from 'types/Layout';
import { useFirebase } from 'hooks/useFirebase';
import { Form, RHFFormInput } from 'components/common/Form/v2';

const schema = Joi.object({
  email: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
});

type ForgotPasswordInputs = {
  email: string;
};

export const ForgotPassword = (): ReactElement => {
  const { sendResetPasswordMail } = useAuthApi();
  const [isReset, setIsReset] = useState(false);
  const [resetMail, setResetMail] = useState('');
  const { logFirebaseUserFlow } = useFirebase();

  return (
    <PageContext variant={InAppLayoutVariant.TERTIARY}>
      {isReset ? (
        <VStack gap="xl" mt="10" px={{ base: 'l', md: 0 }}>
          <CheckIconLarge w="35vw" h="96px" mb="10" />
          <Text textStyle="h1.medium" textAlign="center" color="text.primary">
            Du hast Post von uns!
          </Text>
          <Text textAlign="center" textStyle="h4.regular" color="text.quaternary">
            Danke! Wir haben dir eine Mail geschickt, mit Infos dazu wie du dein Passwort
            zurücksetzen kannst.
          </Text>
          <Text textStyle="h3.medium" color="text.accent" pb="10">
            {resetMail}
          </Text>
          <Button variant="primary" w="full">
            <Link to="/auth/login">Zum Login</Link>
          </Button>
        </VStack>
      ) : (
        <VStack align="left" mt="10" gap="5" px={{ base: 'l', md: 0 }}>
          <Text as="h1" textStyle="h1.medium" color="text.primary">
            Passwort vergessen?
          </Text>
          <Text color="text.secondary" textStyle="body.s.regular">
            Bitte trage hier die E-Mail-Adresse ein, mit der du dich angemeldet hast. Wir werden dir
            anschließend den Link zum Zurücksetzen deines Passworts zusenden.
          </Text>
          <Form<ForgotPasswordInputs, typeof schema>
            onSubmit={async (values: { email: string }) => {
              const resetData = await sendResetPasswordMail(values);
              if (resetData === 201) {
                setResetMail(values.email);
                setIsReset(true);
                logFirebaseUserFlow('forgot_password');
              }
            }}
            schema={schema}
            autoComplete="on"
          >
            <RHFFormInput
              type="email"
              placeholder="E-Mail"
              name="email"
              leftElement={<NovaIcon icon={EmailIcon} size="s" aria-hidden />}
            />
            <Stack align="center">
              <Button variant="primary" type="submit" w="full" mt="10">
                Zurücksetzen
              </Button>
            </Stack>
          </Form>
        </VStack>
      )}
    </PageContext>
  );
};
